import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckIcon, ShieldCheckIcon, LockIcon, LibraryIcon, SparklesIcon, BookOpenIcon, PaletteIcon, ScissorsIcon, ClipboardListIcon, BriefcaseIcon } from 'lucide-react';
interface UpsellPopupProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onClose: () => void;
}
export function UpsellPopup({
  isOpen,
  onAccept,
  onDecline,
  onClose
}: UpsellPopupProps) {
  return <AnimatePresence data-id="element-353">
      {isOpen && <>
          {/* Overlay */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70]" onClick={onClose} data-id="element-354" />

          {/* Popup */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 25
      }} className="fixed inset-0 z-[71] flex items-center justify-center p-4 pointer-events-none" data-id="element-355">
            <div className="relative bg-surface border border-white/10 rounded-3xl shadow-2xl shadow-black/60 max-w-lg w-full max-h-[90vh] overflow-y-auto pointer-events-auto" onClick={e => e.stopPropagation()} data-id="element-356">
              {/* Close button */}
              <button onClick={onClose} className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-background/80 border border-white/10 flex items-center justify-center text-textMuted hover:text-text transition-colors" data-id="element-357">
                <XIcon className="w-4 h-4" data-id="element-358" />
              </button>

              {/* Header Banner */}
              <div className="bg-danger rounded-t-3xl px-6 py-4 text-center relative overflow-hidden" data-id="element-359">
                <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent,transparent_10px,rgba(0,0,0,0.1)_10px,rgba(0,0,0,0.1)_20px)]" data-id="element-360"></div>
                <div className="relative z-10" data-id="element-361">
                  <span className="inline-block bg-white/20 text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-2 upsell-flash-badge" data-id="element-362">
                    OFERTA EXCLUSIVA — SOLO EN ESTA PANTALLA
                  </span>
                  <h3 className="font-serif text-xl sm:text-2xl text-white font-bold leading-tight" data-id="element-363">
                    ESPERA — No te vayas sin esto...
                  </h3>
                  <p className="text-white/80 text-sm mt-1" data-id="element-364">
                    Una oferta irrepetible que no volverás a ver
                  </p>
                </div>
              </div>

              {/* Body */}
              <div className="px-6 py-6 space-y-5" data-id="element-365">
                {/* Flashing discount badge */}
                <div className="text-center" data-id="element-366">
                  <span className="inline-block bg-accent/15 border border-accent/30 text-accent font-bold text-lg px-5 py-2 rounded-full upsell-flash-badge" data-id="element-367">
                    30% DE DESCUENTO EN EXTRAS
                  </span>
                </div>

                {/* Main copy */}
                <p className="text-text text-center leading-relaxed" data-id="element-368">
                  Llévate el{' '}
                  <strong className="text-accent" data-id="element-369">
                    Paquete Completo Sculpt-It
                  </strong>{' '}
                  con las 50 plantillas premium y la guía de acabado profesional
                  "Studio Glow" — todo lo que necesitas para crear una galería
                  completa en casa y que tus piezas se vean de revista.
                </p>
                <p className="text-textMuted text-sm text-center" data-id="element-370">
                  Este bundle incluye el sistema completo + todos los extras a
                  un precio que no estará disponible después de cerrar esta
                  ventana.
                </p>

                {/* What's included */}
                <div className="bg-background rounded-xl border border-white/10 p-4 space-y-2.5" data-id="element-371">
                  <p className="text-xs uppercase tracking-widest text-accent font-bold mb-3 text-center" data-id="element-372">
                    Todo lo que recibes:
                  </p>
                  <div className="space-y-2 text-sm" data-id="element-373">
                    <div className="flex items-center gap-2 text-text" data-id="element-374">
                      <BookOpenIcon className="w-4 h-4 text-accent flex-shrink-0" data-id="element-375" />
                      <span data-id="element-376">
                        Ebook Maestro "Sculpt-It"{' '}
                        <span className="text-textMuted" data-id="element-377">(Valor $27)</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-text" data-id="element-378">
                      <PaletteIcon className="w-4 h-4 text-accent flex-shrink-0" data-id="element-379" />
                      <span data-id="element-380">
                        Psicología del Color DIY{' '}
                        <span className="text-textMuted" data-id="element-381">(Valor $17)</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-text" data-id="element-382">
                      <ScissorsIcon className="w-4 h-4 text-accent flex-shrink-0" data-id="element-383" />
                      <span data-id="element-384">
                        Plantilla Gigante T-Rex{' '}
                        <span className="text-textMuted" data-id="element-385">(Valor $15)</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-text" data-id="element-386">
                      <ClipboardListIcon className="w-4 h-4 text-accent flex-shrink-0" data-id="element-387" />
                      <span data-id="element-388">
                        Checklist de Ahorro 70%{' '}
                        <span className="text-textMuted" data-id="element-389">(Valor $7)</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-text" data-id="element-390">
                      <BriefcaseIcon className="w-4 h-4 text-accent flex-shrink-0" data-id="element-391" />
                      <span data-id="element-392">
                        El Negocio del Papercraft{' '}
                        <span className="text-textMuted" data-id="element-393">(Valor $27)</span>
                      </span>
                    </div>
                    <div className="border-t border-white/10 my-2" data-id="element-394"></div>
                    <div className="flex items-center gap-2 text-accent font-medium" data-id="element-395">
                      <LibraryIcon className="w-4 h-4 flex-shrink-0" data-id="element-396" />
                      <span data-id="element-397">
                        50 Plantillas Premium de Autor{' '}
                        <span className="text-textMuted" data-id="element-398">(Valor $9.99)</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-accent font-medium" data-id="element-399">
                      <SparklesIcon className="w-4 h-4 flex-shrink-0" data-id="element-400" />
                      <span data-id="element-401">
                        Guía "Studio Glow" de Acabado Pro{' '}
                        <span className="text-textMuted" data-id="element-402">(Valor $9.99)</span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Pricing block */}
                <div className="text-center space-y-2 py-2" data-id="element-403">
                  <div className="flex items-center justify-center gap-2" data-id="element-404">
                    <LockIcon className="w-4 h-4 text-accent" data-id="element-405" />
                    <span className="text-xs text-textMuted uppercase tracking-widest" data-id="element-406">
                      Precio especial solo en esta pantalla
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-3" data-id="element-407">
                    <span className="text-2xl text-textMuted line-through decoration-danger decoration-2" data-id="element-408">
                      $29.97
                    </span>
                    <span className="text-4xl font-serif font-bold text-text" data-id="element-409">
                      $23<span className="text-3xl text-accent" data-id="element-410">.98</span>
                    </span>
                  </div>
                  <p className="text-xs text-accent font-medium" data-id="element-411">
                    Ahorras $5.99 con este paquete exclusivo
                  </p>
                  <div className="flex items-center justify-center gap-1.5 text-xs text-textMuted" data-id="element-412">
                    <ShieldCheckIcon className="w-3.5 h-3.5 text-accent" data-id="element-413" />
                    <span data-id="element-414">Garantía de 7 días — Sin riesgo</span>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="space-y-3" data-id="element-415">
                  <button onClick={onAccept} className="w-full group inline-flex items-center justify-center gap-2 bg-accent text-background px-6 py-4 rounded-xl font-bold text-base sm:text-lg transition-all hover:bg-accentHover hover:scale-[1.02] active:scale-[0.98] shadow-[0_0_40px_rgba(212,168,83,0.35)] upsell-pulse-btn" data-id="element-416">
                    <CheckIcon className="w-5 h-5" data-id="element-417" />
                    <span data-id="element-418">SÍ, QUIERO EL PAQUETE COMPLETO POR $23.98</span>
                  </button>

                  <button onClick={onDecline} className="w-full text-center text-sm text-textMuted/60 hover:text-textMuted transition-colors py-2 underline underline-offset-2 decoration-textMuted/30" data-id="element-419">
                    No gracias, continuar sin los extras
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}